import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { createUser } from "../api/createUser";
import "./CreateUserForm.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const HIDDEN_FIELDS_BY_GROUP = {
  "660680ecb9eb923075c7c977": ["Address", "Department"],
};

const ALLOWED_CUSTOM_FIELDS = [
  "City",
  "State",
  "Country",
  "Job Title",
  "Department",
  "Compliance Group",
  "Work Environment",
];

const isValidMongoId = (id) => /^[a-f\d]{24}$/i.test(id);

/* ---------------- Success Modal ---------------- */
function SuccessModal({ onOk }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content-success">
        <h3>✅ User Created Successfully</h3>
        <div className="modal-buttons">
          <button className="modal-btn" onClick={onOk}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Error Modal ---------------- */
function ErrorModal({ message, onOk }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-heading">⚠️ Error</div>

        <div className="modal-body">
          <p>{message}</p>
        </div>

        <div className="modal-footer">
          <button className="modal-btn" onClick={onOk}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserForm() {
  const { groupId } = useParams();

  /* ---------------- Group State ---------------- */
  const [groupName, setGroupName] = useState("");
  const [groupImage, setGroupImage] = useState("");
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [groupError, setGroupError] = useState("");

  /* ---------------- Form State ---------------- */
  const [formData, setFormData] = useState({
    mail: "",
    password: "",
    firstName: "",
    lastName: "",
    role: "learner",
  });

  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  const [loadingCustomFields, setLoadingCustomFields] = useState(true);
  const [customFieldError, setCustomFieldError] = useState("");

  /* ---------------- Fetch Group ---------------- */
  useEffect(() => {
    console.log("Group ID from URL:", groupId);
    if (!groupId || !isValidMongoId(groupId)) {
      setGroupError(
        "This registration link is invalid or incomplete. Please contact your administrator."
      );
      setLoadingGroup(false);
      return;
    }

    async function fetchGroup() {
      try {
        console.log("Fetching group details for ID:", groupId);
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/groups/${groupId}`,
          {
            params: {
              company: process.env.REACT_APP_COMPANY,
              apiKey: process.env.REACT_APP_APIKEY,
            },
          }
        );

        setGroupName(res.data.name);
        setGroupImage(res.data.imageUrl);
      } catch {
        setGroupError(
          "This registration link is invalid, expired, or you do not have access."
        );
      } finally {
        setLoadingGroup(false);
      }
    }

    fetchGroup();
  }, [groupId]);

  /* ---------------- Fetch Custom Fields ---------------- */
  useEffect(() => {
    async function fetchCustomFields() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/customfields`,
          {
            params: {
              company: process.env.REACT_APP_COMPANY,
              apiKey: process.env.REACT_APP_APIKEY,
            },
          }
        );

        if (!res.data || res.data.length === 0) {
          throw new Error();
        }

        setCustomFields(res.data);
      } catch {
        setCustomFieldError(
          "Unable to load required registration fields. Please try again later."
        );
      } finally {
        setLoadingCustomFields(false);
      }
    }

    fetchCustomFields();
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleCustomChange(fieldId, value) {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await createUser({ ...formData, groupId });

      if (!result.success) {
        throw new Error(result.message);
      }

      const userObj = JSON.parse(result.data.data);
      const userId = userObj._id;

      if (Object.keys(customFieldValues).length > 0) {
        await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/user/update-customfields`,
          {
            userId,
            payload: {
              values: Object.entries(customFieldValues).map(
                ([customFieldId, value]) => ({
                  customFieldId,
                  value,
                })
              ),
            },
          }
        );
      }

      setSuccessModal(true);
    } catch (err) {
      setErrorModal({
        show: true,
        message: err.message || "Registration failed",
      });
    } finally {
      setLoading(false);
    }
  }

  if (loadingGroup || loadingCustomFields) {
    return (
      <div className="page-container">
        <div className="form-card">
          <p>Loading registration...</p>
        </div>
      </div>
    );
  }

  if (customFieldError) {
    return (
      <div className="page-container">
        <div className="form-card">
          <h2>⚠️ Registration Unavailable</h2>
          <p className="error-text">{customFieldError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-card">
        {groupError ? (
          <>
            <h2>⚠️ Registration Error</h2>
            <p className="error-text">{groupError}</p>
          </>
        ) : (
          <>
            {groupImage && (
              <img
                src={groupImage}
                alt={groupName}
                className="group-logo"
              />
            )}

            <h2>Create Your Account</h2>
            <p>
              Join <strong>{groupName}</strong>
            </p>

            <form onSubmit={handleSubmit}>
              <div className="field-row">
                <input
                  name="firstName"
                  placeholder="First Name"
                  required
                  onChange={handleChange}
                />
                <input
                  name="lastName"
                  placeholder="Last Name"
                  required
                  onChange={handleChange}
                />
              </div>

              <input
                name="mail"
                type="email"
                placeholder="Email"
                required
                onChange={handleChange}
              />

              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  required
                  onChange={handleChange}
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              {customFields
                .filter((field) =>
                  ALLOWED_CUSTOM_FIELDS.includes(field.name)
                )
                .map((field) => {
                  const hidden =
                    HIDDEN_FIELDS_BY_GROUP[groupId] || [];
                  if (hidden.includes(field.name)) return null;

                  return (
                    <div
                      className="field-addition"
                      key={field._id}
                    >
                      {field.authorizedValues?.length ? (
                        <select
                          required
                          defaultValue=""
                          onChange={(e) =>
                            handleCustomChange(
                              field._id,
                              e.target.value
                            )
                          }
                        >
                          <option value="" disabled>
                            Select {field.name}
                          </option>
                          {field.authorizedValues.map((val) => (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          required
                          minLength={3}
                          placeholder={`Enter ${field.name}`}
                          onChange={(e) =>
                            handleCustomChange(
                              field._id,
                              e.target.value
                            )
                          }
                        />
                      )}
                    </div>
                  );
                })}

              <button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Account"}
              </button>
            </form>
          </>
        )}
      </div>

      <div className="image-panel"></div>

      {successModal && (
        <SuccessModal
          onOk={() =>
            (window.location.href =
              `${process.env.REACT_APP_BASE_URL}/`)
          }
        />
      )}

      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onOk={() =>
            setErrorModal({ show: false, message: "" })
          }
        />
      )}
    </div>
  );
}

export default CreateUserForm;
